import { formatLeadTimeWeeks } from '@lib/calculations/lead-time/calculate-weeks';
import { Link, Text, View } from '@react-pdf/renderer';
import logo from '@ui/assets/images/BTL/BELLOWSTECH_RGB_PRIMARY_LOGO_AW.png';
import { EnvConfig } from '@ui/config/env';

import { usePDFContext } from '../PDFProvider';
import { EmployeeInformation } from './components/EmployeeInformation';
import { Header } from './components/Header';
import { Logo } from './components/Logo';
import { Page } from './components/Page';
import { CustomerAddress } from './components/QuoteDetails/CustomerAddress';
import { CustomerContact } from './components/QuoteDetails/CustomerContact';
import { QuoteDetails } from './components/QuoteDetails/QuoteDetails';
import { QuoteInformation } from './components/QuoteDetails/QuoteInformation';
import { QuoteId } from './components/QuoteId';
import { QuoteLineForPart, QuoteLineForServices } from './components/QuoteLine';
import { PartId } from './components/QuoteLine/PartId';
import { PartSpecs } from './components/QuoteLine/PartSpecs';
import { SiteInformation } from './components/SiteInformation';
import { Specs } from './components/Specs';
import { styles } from './components/styles';

const BTL_SITE_INFORMATION = [
  '132 Business Center Drive, Ste9',
  'Ormond Beach, FL 32174',
  'Phone (386) 615 - 7530',
];

export function BTLQuoteDocument() {
  const { quoteLines } = usePDFContext();

  return (
    <Page>
      <Header>
        <Logo source={logo} />

        <SiteInformation name="BellowsTech" items={BTL_SITE_INFORMATION} />

        {/* Some sites may have a different ecommerce site */}
        <QuoteId ecommerceUri={EnvConfig.getKey('MW_COMPONENTS_URI')} />
      </Header>

      <QuoteDetails>
        <CustomerAddress />

        <CustomerContact />

        <QuoteInformation />
      </QuoteDetails>

      {quoteLines.map((line) => {
        const { part, ...lineProps } = line;
        if (!part) {
          return <QuoteLineForServices {...line} showDescription />;
        }

        // BTL carries lead time in its own bottom-of-line sentence, so it opts
        // out of both the shared line row and the per-break column and quotes
        // the shortest across the line's prices (see `getSmallestLeadTime`).
        //
        // A quantity-aware line therefore shows only its best case here. BTL is
        // onboarded but not quoting through SNAP yet, and adopting
        // quantity-aware lead times would need this copy rewritten to speak
        // about a range.
        const bottomOfLineText = line.leadTimeWeeks
          ? `Lead Time: ${formatLeadTimeWeeks(line.leadTimeWeeks).toLowerCase()} to ship after acceptance of order; subject to change.`
          : undefined;
        const lineData = {
          ...lineProps,
          bottomOfLineText,
        };
        return (
          <QuoteLineForPart
            key={line.id}
            {...lineData}
            part={part}
            showDrawing
            hideLeadTime
          >
            <PartId part={part} />

            <PartSpecs specs={line.specs} />
          </QuoteLineForPart>
        );
      })}

      <Specs />

      <EmployeeInformation />

      {/* The rest of this is completely custom per site */}
      <View style={styles.column}>
        <View style={[styles.fullRow, styles.widthFull]}>
          <Text
            style={[
              styles.textSmall,
              styles.textCenter,
              styles.bold,
              styles.highlight,
            ]}
          >
            Please direct all PURCHASE ORDERS to: {'   '}
            JKEMPFER@MWCOMPONENTS.COM
          </Text>
          <Text style={[styles.textSmall, styles.textCenter]}>
            ISO 9001: 2015 Certified
          </Text>
          <Text style={[styles.textSmall, styles.textCenter]}>
            Please note: material availability may affect quoted lead-time at
            the time of purchase.
          </Text>
        </View>
      </View>
      <View style={styles.row}>
        <View style={styles.fullRow}>
          <Text style={[styles.textSmall, styles.textCenter]}>
            Standard terms and conditions can be found on{'\n'}
            <Link href="https://www.mwcomponents.com/mw-industries-terms-and-conditions">
              {' '}
              https://www.mwcomponents.com/mw-industries-terms-and-conditions
            </Link>
          </Text>
        </View>
      </View>
    </Page>
  );
}
